import React, { useCallback, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import LanguageSwitcher from "./LanguageSwitcher/LanguageSwitcher";

const NavBar = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const navRef = useRef(null);
  const location = useLocation();
  const { t } = useTranslation();

  const handleMenuToggle = useCallback(() => {
    setMobileMenuOpen((s) => !s);
  }, []);

  const handleItemClick = useCallback(() => {
    setMobileMenuOpen(false);
  }, []);

  const isAirportPage = location.pathname.startsWith("/airport/");
  const isHome = location.pathname === "/";
  const isContacts = location.pathname === "/contacts";

  return (
    <header
      ref={navRef}
      className="glass-nav fixed top-0 left-0 right-0 z-30"
    >
      <div className="px-5 md:px-10 lg:px-20 mx-auto max-w-screen-xl">
        <div className="flex items-center justify-between h-16">
          {/* Logo / back link */}
          {!isHome ? (
            <Link
              to="/"
              onClick={handleItemClick}
              className="flex items-center gap-3 group rounded focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-aviation-cyan"
              aria-label={`${t("app.name")} — ${t("nav.airports")}`}
            >
              <img
                className="w-8 h-8 object-contain"
                src="/logo-small.png"
                alt=""
                aria-hidden="true"
              />
              <span className="text-sm font-bold text-slate-200 group-hover:text-aviation-cyan transition-colors duration-200 hidden sm:block tracking-wide">
                {t("app.name")}
              </span>
            </Link>
          ) : (
            <span aria-hidden="true" />
          )}

          {/* Desktop nav */}
          <nav
            className="hidden md:flex items-center gap-4"
            aria-label={t("nav.ariaLabel")}
          >
            {isAirportPage && (
              <Link
                to="/"
                onClick={handleItemClick}
                className="text-sm font-medium text-slate-400 hover:text-aviation-cyan transition-colors duration-200 px-3 py-1 rounded border border-transparent hover:border-aviation-cyan/30 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-aviation-cyan"
                aria-current={isHome ? "page" : undefined}
              >
                {t("nav.backToAirports")}
              </Link>
            )}
            {!isContacts && !isHome && (
              <Link
                to="/contacts"
                onClick={handleItemClick}
                className="text-sm font-medium text-slate-400 hover:text-aviation-cyan transition-colors duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-aviation-cyan rounded"
                aria-current={isContacts ? "page" : undefined}
              >
                {t("nav.contacts")}
              </Link>
            )}
            <LanguageSwitcher />
          </nav>

          {/* Mobile: language + hamburger */}
          <div className="flex items-center gap-2 md:hidden">
            <LanguageSwitcher />
            {!isHome && (
              <button
                onClick={handleMenuToggle}
                className="p-2 rounded text-slate-400 hover:text-slate-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-aviation-cyan min-w-[44px] min-h-[44px] flex items-center justify-center"
                aria-label={mobileMenuOpen ? t("nav.closeMenu") : t("nav.openMenu")}
                aria-expanded={mobileMenuOpen}
                aria-controls="mobile-menu"
              >
                <svg viewBox="0 0 20 20" className="w-5 h-5" fill="currentColor" aria-hidden="true">
                  {mobileMenuOpen ? (
                    <path
                      fillRule="evenodd"
                      d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                      clipRule="evenodd"
                    />
                  ) : (
                    <path
                      fillRule="evenodd"
                      d="M3 5a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zM3 10a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zM3 15a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1z"
                      clipRule="evenodd"
                    />
                  )}
                </svg>
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Mobile dropdown */}
      {mobileMenuOpen && !isHome && (
        <nav
          id="mobile-menu"
          className="md:hidden border-t border-white/10 bg-aviation-cockpit/95 backdrop-blur-md px-5 py-4 flex flex-col gap-1"
          aria-label={t("nav.mobileAriaLabel")}
        >
          <Link
            to="/"
            onClick={handleItemClick}
            className="text-sm font-medium text-slate-300 hover:text-aviation-cyan py-3 px-2 rounded transition-colors duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-aviation-cyan min-h-[44px] flex items-center"
            aria-current={isHome ? "page" : undefined}
          >
            {t("nav.backToAirports")}
          </Link>
          {!isContacts && (
            <Link
              to="/contacts"
              onClick={handleItemClick}
              className="text-sm font-medium text-slate-300 hover:text-aviation-cyan py-3 px-2 rounded transition-colors duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-aviation-cyan min-h-[44px] flex items-center"
              aria-current={isContacts ? "page" : undefined}
            >
              {t("nav.contacts")}
            </Link>
          )}
        </nav>
      )}
    </header>
  );
};

export default NavBar;
